import { useState } from "react";
import { Table, Progress, Modal, ModalHeader, ModalBody } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./DynamicTable.scss";
import useEllipsisTooltip from "@workspace/common/src/Components/DynamicTable/EllipsisToolTip";
import { Images } from "@workspace/common";
import DynamicTableWrapper from "../InnerDynamicTable/DynamicTableWrapper";

const DynamicTable = ({
  data,
  config,
  pageRequestSet,
  freezeHeader = false,
  isLoading = false,
  activeRow,
  setTableConfig,
  tableHeight = "565px",
  pageRequest = null,
  headerCenter = false,
  isActiveRowIndex = false,
  jobId,
  expandedRow,
  closeTag
}) => {
  const [dataMatchingModal, setDataMatchingModal] = useState(false);
  const [specificData, setSpecificData] = useState({title: '', content: []});
  const dataMatchingToggle = () => setDataMatchingModal(!dataMatchingModal);

  // Expand column width
  const toggleColumnExpand = (configIndex) => {
    setTableConfig((prev) => {
      return prev.map((item, prevIndex) => {
        if (prevIndex === configIndex) {
          if (item?.expand === true) {
            return { ...item, expand: false };
          } else if (item?.expand === false || item?.expand === undefined) {
            return { ...item, expand: true };
          }
          return item;
        }
        return item;
      });
    });
  };

  //Round up value to nearest 5 multiple
  const roundUpToNearest5 = (value) => {
    return Math.min(100, Math.ceil(value * 100 / 5) * 5);
  };

  // progress bar value to color mapping
  const valueToColor = (value) => {
    if (value >= 75) {
      return "success"
    } else if (value >= 50) {
      return "#0004ff"
    } else {
      return "danger"
    }
  }

  // Open Data Matching Modal
  const handleOpenDataMatchingModal = (title, data) => {
    setSpecificData({title: title, content: data});
    setDataMatchingModal(true);
  }

  const HeaderCell = ({
    option,
    configIndex,
    pageRequest,
    pageRequestSet,
    toggleColumnExpand,
  }) => {
    const { textRef, isEllipsisActive } = useEllipsisTooltip(option.header);
    const combinedStyle = {
      ...(option?.expand === true
        ? { overflow: "visible", maxWidth: "100%" }
        : { maxWidth: "320px", minWidth: "50px" }),
    };

    if (option.sort === true) {
      return (
        <th
          key={option.name}
          scope="col"
          className={`dynamic-table-font-size ${
            (option?.sticky === "left" &&
              "sticky-left sticky-color sticky-head-border") ||
            (option?.sticky === "right" &&
              "sticky-right sticky-color sticky-head-border") ||
            "non-sticky-head-border"
          }`}
          onDoubleClick={() => {
            toggleColumnExpand(configIndex);
          }}
          style={combinedStyle}
        >
          <div
            className={`d-flex gap-2 cursor-pointer ${
              option?.center && "justify-content-center"
            } ${headerCenter && "justify-content-center"}`}
            onClick={() => pageRequestSet.setSortAndDirection(option)}
          >
            <span
              ref={textRef}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={isEllipsisActive ? option.header : ""}
            >
              {" "}
              {option.header}
            </span>
            <div className="d-flex flex-column">
              {pageRequest?.sortBy === option.sortValue &&
                pageRequest?.sortDirection === "asc" && (
                  <i
                    className={`ri-sort-asc cursor-pointer`}
                    onClick={() => pageRequestSet.setSortAndDirection(option)}
                  ></i>
                )}
              {pageRequest?.sortBy === option.sortValue &&
                pageRequest?.sortDirection === "desc" && (
                  <i
                    className={`ri-sort-desc cursor-pointer`}
                    onClick={() => pageRequestSet.setSortAndDirection(option)}
                  ></i>
                )}
            </div>
          </div>
        </th>
      );
    } else {
      return (
        <th
          key={option.name}
          scope="col"
          className={`${
            (option?.sticky === "left" &&
              "sticky-left sticky-color sticky-head-border") ||
            (option?.sticky === "right" &&
              "sticky-right sticky-color sticky-head-border") ||
            "non-sticky-head-border"
          } `}
          onDoubleClick={() => {
            toggleColumnExpand(configIndex);
          }}
          style={{
            border: "2px solid black !important",
            ...combinedStyle,
          }}
        >
          <div
            className={`d-flex gap-2 ${
              option?.center && "justify-content-center"
            }`}
          >
            <span ref={textRef} title={isEllipsisActive ? option.header : ""}>
              {" "}
              {option.header}
            </span>
          </div>
        </th>
      );
    }
  };

  // ========================================= Outer Table Configuration ===========================
  // Generate Header
  const generateHeaderJSX = (config) => {
    return (
      <>
        {config?.map((option, configIndex) => (
          <HeaderCell
            key={option.name}
            option={option}
            configIndex={configIndex}
            pageRequest={pageRequest}
            pageRequestSet={pageRequestSet}
            toggleColumnExpand={toggleColumnExpand}
          />
        ))}
      </>
    );
  };

  const generateBodyJSX = (config, data) => {
    return data?.sort((a, b) => b.matchingResult?.total_score - a.matchingResult?.total_score).flatMap((rowData, i) => {
      const isRowChecked = activeRow?.includes(rowData?.id);
      const isRowExpanded = expandedRow === rowData.id;
      const matchingResult = rowData?.matchingResult;

      const returnClass = (isSticky, isRowChecked) => {
        if (isSticky && isRowChecked) {
          return "sticky-color-checked";
        } else if (isSticky) {
          return "sticky-color";
        } else {
          return "";
        }
      };

      const rowdata = config.map((option, configIndex) => {
        const combinedStyle = {
          ...(option?.expand === true
            ? { overflow: "visible", maxWidth: "100%" }
            : { maxWidth: "320px", minWidth: "50px" }),
        };
        return (
          <td
            key={option.name}
            style={combinedStyle}
            className={`dynamic-table-font-size ${
              (option?.sticky === "left" && "sticky-left") ||
              (option?.sticky === "right" && "sticky-right") ||
              ""
            } ${returnClass(option?.sticky, isRowChecked)}`}
            onDoubleClick={() => {
              toggleColumnExpand(configIndex);
            }}
          >
            {option.render(rowData, i)}
          </td>
        );
      });

      // Original Row
      const mainRow = (
        <tr
          className={`${
            isActiveRowIndex
              ? activeRow?.includes(i)
                ? "row-selected"
                : " "
              : activeRow?.includes(rowData?.id)
              ? "row-selected"
              : " "
          }`}
          key={rowData.id}
        >
          {rowdata}
        </tr>
      );

      // Expanded Row (conditionally rendered)
      const expandedRowContent = isRowExpanded && (
        <tr key={rowData.id} className="expanded-row overflow-hidden">
          <td colSpan={config.length} style={{ background: "#F3F8FF" }}>
            {/* Add the content you want to show in the expanded row here */}
            <div className="expanded-content">
              {/* Example expanded content */}
              <div className="w-100 d-flex flex-column align-items-start gap-2">
                <div className="d-flex flex-row align-items-center gap-2">
                  <div className="mapping-ai">
                    <div className="mapping-ai-inner">
                      <img src={Images.sparkle} alt="ai-sparkle" style={{ width: "10px" }} />
                    </div>
                  </div>
                  <span className="mapping-ai-color">Mapping AI</span>
                </div>
                <div className="d-flex flex-row align-items-end justify-content-between w-100">
                  <div className="d-flex flex-column align-items-start mt-2">
                    <span>{rowData?.candidateSubmissionData?.firstName}{" "}
                      {rowData?.candidateSubmissionData?.lastName}
                    </span>
                    <span className="fw-semibold mt-2" style={{fontSize: '11px'}}>% of JD Match</span>
                  </div>
                  <div className="d-flex flex-column">
                    <span style={{fontSize: '11px', color: '#808A9C'}}>Overall Match</span>
                    <div className="d-flex flex-row align-items-center gap-2">
                      <Progress
                        value={roundUpToNearest5(matchingResult?.total_score)}
                        style={{ width: "300px", height: "3px" }}
                        color={valueToColor(roundUpToNearest5(matchingResult?.total_score))}
                      />
                      <span>{roundUpToNearest5(matchingResult?.total_score)} %</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-row align-items-start justify-content-between w-100">
                  <div className="d-flex flex-column gap-2 pe-3">
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                        <span className="mapping-text">Skills</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.skills?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.skills?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.skills?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Skills", matchingResult?.skills?.matches)}/>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">Location</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(
                          isNaN(matchingResult?.location?.matches[0]?.similarity_score) 
                            ? 0 
                            : matchingResult?.location?.matches[0]?.similarity_score
                        ))}
                        value={roundUpToNearest5(
                          isNaN(matchingResult?.location?.matches[0]?.similarity_score) 
                            ? 0 
                            : matchingResult?.location?.matches[0]?.similarity_score
                        )}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(
                          isNaN(matchingResult?.location?.matches[0]?.similarity_score) 
                            ? 0 
                            : matchingResult?.location?.matches[0]?.similarity_score
                        )} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Location", matchingResult?.location?.matches)}/>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">Keyword Matching</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.keywords?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.keywords?.overall_score)} 
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.keywords?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Keyword", matchingResult?.keywords?.matches)}/>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2 pe-3">
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">Experience Level</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.years_of_experience?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.years_of_experience?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.years_of_experience?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Experience Level", matchingResult?.years_of_experience?.matches)}/>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">Job Title/Role</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.job_title?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.job_title?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.job_title?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Title/Role", matchingResult?.job_title?.matches)}/>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">
                          Experience Matching
                        </span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.experience?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.experience?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.experience?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Experience Matching", matchingResult?.experience?.matches)}/>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                        <span className="mapping-text">Education</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.qualifications?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.qualifications?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.qualifications?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Education", matchingResult?.qualifications?.matches)}/>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center"
                    >
                      <div className="mapping-text-wrapper">
                      <span className="mapping-text">Salary Expectations</span>
                      </div>
                      <Progress
                        className="mapping-progress-inner me-3"
                        color={valueToColor(roundUpToNearest5(matchingResult?.salary?.overall_score))}
                        value={roundUpToNearest5(matchingResult?.salary?.overall_score)}
                      />
                      <div className="mapping-percent-wrapper me-2">
                        <span>{roundUpToNearest5(matchingResult?.salary?.overall_score)} %</span>
                      </div>
                      <i className="mdi mdi-information-outline cursor-pointer" onClick={() => handleOpenDataMatchingModal("Salary Expectation", matchingResult?.salary?.matches)}/>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column mt-3 w-100">
                  <span>Explore Similar Job Listings</span>
                  <div>
                    <DynamicTableWrapper candidateData={rowData} jobId={jobId} expandedRow={expandedRow} closeTag={closeTag}/>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      );

      return [mainRow, expandedRowContent].filter(Boolean);
    });
  };
  // ================================================================================================
  return (
    <>
      {/* Table */}
      <div
        className="table-responsive table-hover table-card mt-3 mb-1 table-custom"
        style={{
          maxHeight: tableHeight,
        }}
      >
        <Table
          className="m-0 align-middle dynamic-table-main"
          id="accountListingTable"
          style={{
            tableLayout: "auto",
            position: "sticky !important",
            zIndex: 1,
            top: 0,
          }}
        >
          <thead
            className={`${freezeHeader ? "sticky-head" : "non-sticky-head"}`}
          >
            <tr className="text-dark">{data && generateHeaderJSX(config)}</tr>
          </thead>
          <tbody className="list form-check-all">
            {isLoading ? (
              <tr>
                <td colSpan={config?.length}>
                  <Skeleton className="pb-3" count={2} />
                </td>
              </tr>
            ) : data?.length > 0 ? (
              generateBodyJSX(config, data)
            ) : (
              <tr>
                <td
                  className="dynamic-table-font-size"
                  colSpan={config?.length}
                >
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Modal isOpen={dataMatchingModal} toggle={dataMatchingToggle} className="data-matching-modal">
        <ModalHeader toggle={dataMatchingToggle}>{specificData?.title}</ModalHeader>
        <ModalBody className="overflow-auto">
          <Table>
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "40%" }}>Job {specificData?.title}</th>
                <th style={{ width: "40%" }}>Candidate {specificData?.title}</th>
                <th style={{ width: "15%" }}>Score</th>
              </tr>
            </thead>
            <tbody>
            {specificData?.content?.sort((a, b) => b?.similarity_score - a?.similarity_score).map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item?.source_item}</td>
                <td>{item?.target_item}</td>
                <td>{Math.min(100, roundUpToNearest5(item?.similarity_score))} %</td>
              </tr>
            ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DynamicTable;
